<template>
    <div class="p-day-one-reserving-simple">
        <div class="a-loader--big" v-if="calculating"></div>
        <div v-else-if="!hasResults">
            <h2 class="u-margin-top-10 u-margin-bottom-10">Your Results</h2>
            <p>Your results will appear here once all mandatory fields have been completed.</p>
        </div>
        <div v-else>
            <accordion>
                <accordion-item show-open>
                    <h2 slot="header">Facts &amp; Figures</h2>
                    <table class="a-table u-full-width">
                        <tr>
                            <td>Age at Onset</td>
                            <td width="150">{{ results.calculation.ageAtOnset }}</td>
                        </tr>
                        <tr>
                            <td>Age at Assumed Death </td>
                            <td>{{ roundValue(results.calculation.ageAtAssumedDeath, 2) }}</td>
                        </tr>
                        <tr>
                            <td>Age at Assumed Settlement</td>
                            <td>{{ roundValue(results.calculation.ageAtAssumedSettlement, 2) }}</td>
                        </tr>
                        <tr>
                            <td>'But for' Expected Age at End of Life</td>
                            <td>{{ roundValue(results.calculation.ageAtExpectedEndOfLife, 2) }} </td>
                        </tr>
                        <tr>
                            <td>Period Between Onset and Assumed Date of Death </td>
                            <td>{{ roundValue(results.calculation.periodBetweenOnsetAssumedDod,2) }}</td>
                        </tr>
                        <tr>
                            <td>
                                <div class="u-d-flex u-flex-space-between">
                                     Loss of Income Ogden Table 36 multiplier
                                    <calculation-modal name="dayOneTable28Multiplier" type="lost">
                                        <icon-tooltip icon-type="info" tooltip-text="Click to view calculations"
                                                      side="right" text-width="140"></icon-tooltip>
                                    </calculation-modal>
                                </div>
                            </td>
                            <td>{{roundValue(results.calculation.table28Multiplier, 2)}}</td>
                        </tr>
                    </table>
                </accordion-item>
                <div class="u-position-relative">
                    <accordion-item>
                        <h2 slot="header">Assumptions</h2>

                        <div class="p-day-one__tooltip-modal">
                            <modal-in-the-wild modal-name="assumptions_overview">
                                <icon-tooltip icon-type="info" tooltip-text="Click to view assumptions"
                                              side="right" text-width="140"></icon-tooltip>
                            </modal-in-the-wild>
                        </div>

                        <ul class="p-day-one__tooltip-modal--assumptions-list">
                            <li>
                                Claimant had normal life expectancy as per the 0% column Ogden Table
                                {{ gender === 'male' ? 1 : 2 }}
                            </li>
                            <li v-if="employmentType==='employed'">
                                The claimant will retire at the state pension age. As the claimant was born on
                                {{dateOfBirth}} this is
                                assumed to be on {{results.calculation.retirementDate}} at age {{retirementAge}}
                            </li>
                            <li>Past loss of income will commence in full from the date of onset of symptoms</li>
                            <li>Typical heads of loss are applied as follows:
                                <ul class="sub">
                                    <li>General damages (PSLA) {{ currencyFormat(results.damages.generalDamages) }}</li>
                                    <li>Care {{ currencyFormat(results.damages.care) }}</li>
                                    <li>
                                        Miscellaneous (medication, travel, appliances, additional food &amp; energy
                                        costs) {{currencyFormat(results.damages.miscellaneous)}}
                                    </li>
                                </ul>
                            </li>
                            <div v-if="estateOrDependency != 'estate'">
                                <li>The future loss of income multiplier is based on Ogden Table 36</li>
                                <li>The dependency multiplicand on services is assesed at £1,500</li>
                                <!--<li>-->
                                    <!--Future dependency on services is determined from date of death to the claimant's-->
                                    <!--"but for" life expectancy-->
                                <!--</li>-->
                                <li>Loss of pension is assessed at £10,000 per annum</li>
                            </div>
                        </ul>
                    </accordion-item>
                </div>
                <div class="u-position-relative">
                    <accordion-item>
                        <h2 slot="header">Total Damages</h2>

                        <div class="a-table--short-no-style">
                            <table class="a-table u-full-width">
                                <tbody>
                                <tr>
                                    <th>Heads of Loss</th>
                                    <th colspan="2">Valuation</th>
                                </tr>
                                <tr>
                                    <td>
                                        <div class="u-d-flex u-flex-space-between">
                                            General Damages (PSLA)
                                        </div>
                                    </td>
                                    <td colspan="2">
                                        <div class="a-form-inputs--pound">
                                            <input type="number" v-model.number="generalDamages"/>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <div class="u-d-flex u-flex-space-between">
                                            Past Loss of Income to Date of Trial
                                            <calculation-modal name="dayOnePastLossOfEarnings" type="lost">
                                                <icon-tooltip icon-type="info" tooltip-text="Click to view calculations"
                                                            side="right" text-width="140"></icon-tooltip>
                                            </calculation-modal>
                                        </div>
                                    </td>
                                    <td colspan="2">{{currencyFormat(results.damages.pastLossOfEarnings)}}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <div class="u-d-flex u-flex-space-between">
                                            Past Cost of DIY/Services
                                            <calculation-modal name="dayOnePastCostOfServices" type="lost">
                                                <icon-tooltip icon-type="info" tooltip-text="Click to view calculations"
                                                            side="right" text-width="140"></icon-tooltip>
                                            </calculation-modal>
                                        </div>
                                    </td>
                                    <td colspan="2">{{currencyFormat(results.damages.pastCostOfDiy)}}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <div class="u-d-flex u-flex-space-between">
                                            Miscellaneous (treatments/increased utilities/clothing/foods/appliances &amp;
                                            equipment)
                                            <calculation-modal name="dayOneMiscellaneousCalc" type="lost">
                                                <icon-tooltip icon-type="info" tooltip-text="Click to view calculations"
                                                            side="right" text-width="140"></icon-tooltip>
                                            </calculation-modal>
                                        </div>
                                    </td>
                                    <td colspan="2">{{currencyFormat(results.damages.miscellaneous)}}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <div class="u-d-flex u-flex-space-between">
                                            Care
                                            <calculation-modal name="dayOneCostOfCare" type="lost">
                                                <icon-tooltip icon-type="info" tooltip-text="Click to view calculations"
                                                            side="right" text-width="140"></icon-tooltip>
                                            </calculation-modal>
                                        </div>
                                    </td>
                                    <td colspan="2">{{currencyFormat(results.damages.care)}}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <div class="u-d-flex u-flex-space-between">
                                            Future Cost of DIY/Services
                                            <calculation-modal name="dayOneFutureCostOfServices" type="lost">
                                                <icon-tooltip icon-type="info" tooltip-text="Click to view calculations"
                                                              side="right" text-width="140"></icon-tooltip>
                                            </calculation-modal>
                                        </div>
                                    </td>
                                    <td colspan="2">{{currencyFormat(results.damages.futureCostOfDiy)}}</td>
                                </tr>
                                <tr v-if="estateOrDependency != 'estate'">
                                    <td>
                                        <div class="u-d-flex u-flex-space-between">
                                            Future Loss of Income and Pension Post-Trial & Lost Years
                                            <calculation-modal name="dayOneDependencyOnIncome" type="lost">
                                                <icon-tooltip icon-type="info" tooltip-text="Click to view calculations"
                                                            side="right" text-width="140"></icon-tooltip>
                                            </calculation-modal>
                                        </div>
                                    </td>
                                    <td colspan="2">{{currencyFormat(results.damages.dependencyOnIncome)}}</td>
                                </tr>
                                <!--<tr v-if="estateOrDependency != 'estate'">-->
                                    <!--<td>-->
                                        <!--<div class="u-d-flex u-flex-space-between">-->
                                            <!--Dependency on Services-->
                                            <!--<calculation-modal name="dayOneDependencyOnServices" type="lost">-->
                                                <!--<icon-tooltip icon-type="info" tooltip-text="Click to view calculations"-->
                                                            <!--side="right" text-width="140"></icon-tooltip>-->
                                            <!--</calculation-modal>-->
                                        <!--</div>-->
                                    <!--</td>-->
                                    <!--<td colspan="2">{{currencyFormat(results.damages.dependencyOnServices)}}</td>-->
                                <!--</tr>-->
                                </tbody>
                                <tbody>
                                <tr>
                                    <td colspan="3" style="text-align: center">
                                        <button class="a-button" @click="addHeadOfLoss">
                                            {{ownHeadsOfLoss.length ?
                                            'Add another head of loss' : 'Click here to add your own head of loss'}}
                                        </button>
                                    </td>
                                </tr>
                                <own-head-of-loss v-for="(head, index) in ownHeadsOfLoss"
                                                :head-of-loss="head"
                                                :index="index"
                                                type="lost">
                                </own-head-of-loss>
                                </tbody>
                                <tfoot>
                                <tr>
                                    <td>Total Damages</td>
                                    <td colspan="2">{{currencyFormat(totalDamages)}}</td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </accordion-item>
                </div>
                <accordion-item>
                    <h2 slot="header">Total Reserve</h2>
                    <table class="a-table u-full-width">
                        <tbody>
                        <tr>
                            <td>Total Damages</td>
                            <td colspan="2">{{currencyFormat(totalDamages)}}</td>
                        </tr>
                        <tr>
                            <td>Claimant Costs</td>
                            <td colspan="2">
                                <div class="a-form-inputs--pound">
                                    <input type="number" min="0" v-two-dp :value="claimantCosts" @input="updateClaimantCosts">
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>Defence Costs</td>
                            <td colspan="2">
                                <div class="a-form-inputs--pound">
                                    <input type="number" min="0" v-two-dp :value="defenceCosts" @input="updateDefenceCosts">
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>% Contribution to Damages</td>
                            <td colspan="2"><input type="number" min="0" max="100" :value="damagesContribution"
                                                   @input="updateDamagesContribution"></td>
                        </tr>
                        <tr>
                            <td>% Contribution to Claimant Costs</td>
                            <td colspan="2"><input type="number" min="0" max="100" :value="claimantContribution"
                                                   @input="updateClaimantContribution"></td>
                        </tr>
                        <tr>
                            <td>% Contribution to Defence Costs</td>
                            <td colspan="2"><input type="number" min="0" max="100" :value="defenceContribution"
                                                   @input="updateDefenceContribution"></td>
                        </tr>
                        <tr>
                            <td>Damages Reserve (Apportioned)</td>
                            <td colspan="2">{{currencyFormat(apportionedDamagesReserve)}}</td>
                        </tr>
                        <tr>
                            <td>Claimant Costs (Apportioned)</td>
                            <td colspan="2">{{currencyFormat(apportionedClaimantReserve)}}</td>
                        </tr>
                        <tr>
                            <td>Defence Costs (Apportioned)</td>
                            <td colspan="2">{{currencyFormat(apportionedDefenceReserve)}}</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr class="">
                            <td>Total Reserve</td>
                            <td colspan="2">{{currencyFormat(totalReserve)}}</td>
                        </tr>
                        </tfoot>
                    </table>
                </accordion-item>
                <accordion-item>
                    <h2 slot="header">Compare Your Claim</h2>
                    <div class="p-day-one-reserving-simple__compare-your-claim">
                        <modal-in-the-wild modal-name="compare_your_claim">
                            <icon-tooltip icon-type="info" tooltip-text="Click to view compare details"
                                            side="right" text-width="140"/>
                        </modal-in-the-wild>
                    </div>
                    <reserve-performance-chart slot="if-slot"></reserve-performance-chart>
                </accordion-item>
            </accordion>
            <day-one-save></day-one-save>
            <button class="a-button" v-if="exists" @click="openSummaryDocument">
                Download Summary
            </button>

            <modal name="assumptions_overview" no-default-button="true">
                <h1>Assumptions</h1>
                <accordion>
                    <past-services-modal :value="results.damages.pastCostOfDiy"></past-services-modal>
                    <past-loss-earnings-modal :value="results.damages.pastLossOfEarnings"></past-loss-earnings-modal>
                    <psla-modal :age="assumedLifeExpectancy.ageAtDeath" :duration="durationOfSymptoms"
                                :value="results.damages.generalDamages"></psla-modal>
                    <care-modal :value="results.damages.care" :person="modalPayload.person" :claim="modalPayload.claim" :type="estateOrDependency"></care-modal>
                    <misc-modal :value="results.damages.miscellaneous"></misc-modal>
                    <loss-of-income-modal :value="results.damages.dependencyOnIncome"></loss-of-income-modal>
                </accordion>
            </modal>

            <modal name="calculation_modal" no-default-button="true">
                <modal-content></modal-content>
            </modal>

            <modal name="psla_modal" no-default-button="true">
                <h2>
                    General Damages
                </h2>

                <psla-modal-content :age="assumedLifeExpectancy.ageAtDeath" :duration="durationOfSymptoms"
                                    :value="results.damages.generalDamages"></psla-modal-content>
            </modal>
            <modal name="compare_your_claim" no-default-button="true">
                <h2>
                    Compare your claims
                </h2>
                <p>
                    Using the information you have provided we have attempted to match claims with similar
                    characteristics from your book of claims.
                </p>
                <p>
                    To determine a match we have looked for claims that match:
                    <ul>
                        <li>Disease type: <span class="u-bold">{{diseaseType}}</span></li>
                        <li>Claim type: <span class="u-bold">Lost Years</span></li>
                        <li>Deceased Gender: <span class="u-bold">{{gender==='male'?'Male':'Female'}}</span></li>
                    </ul>
                </p>
            </modal>
        </div>
    </div>
</template>

<script>
    import Accordion from 'components/accordion/Accordion';
    import accordionContent from './accordian-content';
    import AccordionItem from 'components/accordion/AccordionItem';
    import BarChart from 'components/graphs/BarChart';
    import CalculationModal from "../CalculationModal";
    import currencyFormat from 'mixins/currencyFormat';
    import DayOneSave from './DayOneSave';
    import IconTooltip from 'components/IconTooltip';
    import Modal from 'components/modal/Modal';
    import ModalContent from 'components/modal/ModalContent';
    import ModalInTheWild from 'components/modal/ModalInTheWild';
    import moment from 'moment';
    import OwnHeadOfLoss from "../OwnHeadOfLoss";
    import PslaModalContent from './accordian-content/PslaModalContent';
    import ReservePerformanceChart from '../ReservePerformanceChart';
    import roundValue from 'mixins/roundValue';
    import {mapActions, mapGetters} from 'vuex';
    import BellChart from "../BellChart";
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';

    export default {
        name: 'day-one-lost-results',
        components: {
            ...accordionContent,
            BellChart,
            Accordion,
            AccordionItem,
            BarChart,
            CalculationModal,
            DayOneSave,
            IconTooltip,
            Modal,
            ModalContent,
            ModalInTheWild,
            PslaModalContent,
            OwnHeadOfLoss,
            ReservePerformanceChart
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        computed: {
            ...mapGetters('user', [
                'getToken',
            ]),
            ...mapGetters('reserves/dayOneLost', [
                'hasResults',
                'results',
                'ageAtDeath',
                'ageAtAssessment',
                'claimantContribution',
                'defenceContribution',
                'damagesContribution',
                'apportionedClaimantReserve',
                'apportionedDefenceReserve',
                'apportionedDamagesReserve',
                'totalReserve',
                'chartData',
                'gender',
                'durationOfSymptoms',
                'dateOfDeath',
                'ownHeadsOfLoss',
                'totalDamages',
                'estateOrDependency',
                'exists',
                'calculating',
                'claimId',
                'employmentType',
                'dateOfBirth',
                'modalPayload',
                'diseaseType',
                'assumedLifeExpectancy',
                'defenceCosts',
                'claimantCosts'
            ]),
            ...mapGetters('http', [
                'apiRouteRequestRunning'
            ]),
            retirementAge() {
                return this.roundValue(moment(this.results.calculation.retirementDate, 'DD/MM/YYYY')
                    .diff(moment(this.dateOfBirth, 'DD/MM/YYYY'), 'years', true), 2);
            },

          generalDamages: {
              get() {
                  return this.results.damages.generalDamages;
              },
              set(value) {
                  return this.updateValue({
                    key: 'generalDamages',
                    value
                  });
              }
          }

        },
        mixins: [
            currencyFormat,
            roundValue
        ],
        data() {
            return {
                assumptions: false
            }
        },
        methods: {
            ...mapActions('reserves/dayOneLost', [
                'updateValue',
                'save',
                'addHeadOfLoss'
            ]),
            updateClaimantContribution(event) {
                let value = parseFloat(event.target.value);
                value = this._smoothPercentile(value);
                return this.updateValue({
                    key: 'claimantContribution',
                    value
                });
            },
            updateDefenceContribution(event) {
                let value = parseFloat(event.target.value);
                value = this._smoothPercentile(value);
                return this.updateValue({
                    key: 'defenceContribution',
                    value
                });
            },
            updateDamagesContribution(event) {
                let value = parseFloat(event.target.value);
                value = this._smoothPercentile(value);
                return this.updateValue({
                    key: 'damagesContribution',
                    value
                });
            },
            updateClaimantCosts(event) {
                let value = parseFloat(event.target.value);
                return this.updateValue({
                    key: 'claimantCosts',
                    value
                });
            },
            updateDefenceCosts(event) {
                let value = parseFloat(event.target.value);
                return this.updateValue({
                    key: 'defenceCosts',
                    value
                });
            },
            openSummaryDocument() {
                window.open('/api/v1/day-one-summary/pdf/' + this.claimId + '?access_token=' + this.getToken);
            },
            _smoothPercentile: function (value) {
                if (value > 100) {
                    value = 100;
                }
                if (value < 0) {
                    value = 0;
                }
                return value;
            }
        }
    }
</script>



// WEBPACK FOOTER //
// Results.vue?25e8783f